import type { NotificationFormData } from "Components/Notifications/NotificationsManagement/NotificationForm/schema";
import { Bank, type NotificationDto } from "Infrastructure/Api/Api";
import { Resources } from "Translations/Resources";
import type { CodeListItem } from "Models/ICodeListDto";
import type { TFunction } from "Translations/Resources";
import { NotificationVariant } from "Infrastructure/Api/Api";
import { MS_TIME } from "Utils/TimeUtils";
import { AccessRoleCodes, type AccessRoleCode } from "Models/AccessRoleCodes";

const DEFAULT_NOTIFICATION_LINK = "https://app.mo.one";

const getLocalDate = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`; // YYYY-MM-DD
};

const getLocalTime = (time: Date) => time.toTimeString().split(" ")[0]; // HH:MM:SS

export const getDefaultValues = (
  isEditMode: boolean,
  notificationData?: NotificationDto,
): NotificationFormData => {
  if (isEditMode && notificationData) {
    const scheduledDate = new Date(notificationData.scheduledDate);
    const expirationDate = notificationData.expirationDate
      ? new Date(notificationData.expirationDate)
      : null;

    return {
      title: notificationData.title,
      content: notificationData.content,
      variant: notificationData.variant,
      durationType: expirationDate ? "period" : "dismiss",
      duration: {
        from: {
          date: getLocalDate(scheduledDate),
          time: getLocalTime(scheduledDate),
        },
        to: expirationDate
          ? {
              date: getLocalDate(expirationDate),
              time: getLocalTime(expirationDate),
            }
          : null,
      },
      userTypes: [],
      banks: [],
      link: DEFAULT_NOTIFICATION_LINK,
    };
  }

  return {
    title: "",
    content: "",
    variant: NotificationVariant.Announcement,
    durationType: "dismiss",
    duration: {
      from: {
        date: getLocalDate(new Date(Date.now() + 5 * MS_TIME.minute)),
        time: getLocalTime(new Date(Date.now() + 5 * MS_TIME.minute)),
      },
      to: null,
    },
    userTypes: [], //TODO: will be implemented later
    banks: [], //TODO: will be implemented later
    link: DEFAULT_NOTIFICATION_LINK, //TODO: will be implemented later
  };
};

export const getDurationTypeOptions = (
  t: TFunction,
): Array<CodeListItem<string>> => [
  {
    code: "dismiss",
    name: t(Resources.Pages.Content.NotificationForm.DurationType.Dismiss),
  },
  {
    code: "period",
    name: t(Resources.Pages.Content.NotificationForm.DurationType.Period),
  },
];

export const getUserTypeOptions = (
  t: TFunction,
): Array<CodeListItem<AccessRoleCode>> => [
  {
    code: AccessRoleCodes.Customer,
    name: t(Resources.Common.UserType.Customer),
  },
  {
    code: AccessRoleCodes.Merchant,
    name: t(Resources.Common.UserType.Merchant),
  },
  { code: AccessRoleCodes.Hunter, name: t(Resources.Common.UserType.Hunter) },
];

export const getBankOptions = (t: TFunction): Array<CodeListItem<Bank>> => [
  { code: Bank.CSOB, name: t(Resources.BankNames[Bank.CSOB]) },
  { code: Bank.CSAS, name: t(Resources.BankNames[Bank.CSAS]) },
  { code: Bank.KB, name: t(Resources.BankNames[Bank.KB]) },
  { code: Bank.AirBank, name: t(Resources.BankNames[Bank.AirBank]) },
  { code: Bank.UCB, name: t(Resources.BankNames[Bank.UCB]) },
  { code: Bank.MONETA, name: t(Resources.BankNames[Bank.MONETA]) },
  { code: Bank.FIO, name: t(Resources.BankNames[Bank.FIO]) },
  { code: Bank.MBank, name: t(Resources.BankNames[Bank.MBank]) },
  { code: Bank.RB, name: t(Resources.BankNames[Bank.RB]) },
];
